import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { streamAIChat, approvePlan, rejectPlan } from "@/lib/aiApi";

let idCounter = 0;
const nextId = () => `msg-${Date.now()}-${idCounter++}`;

// Requirement: "On the first AI conversation of a session only, begin
// with a friendly greeting instead of an empty chat window." Gated by
// sessionStorage (cleared when the browser tab/session ends) so it only
// ever appears once per session, never repeats on "Start new chat", and
// is purely a client-side bubble (never sent to/stored on the backend).
const SESSION_GREETED_KEY = "ai_office_manager_greeted_session";
const WELCOME_GREETING =
  "\uD83D\uDC4B Welcome! I'm your AI Office Manager.\n\n" +
  "I can help with scheduling, payments, instructor availability, student progress, forms, reports, communication drafts, and operational questions.\n\n" +
  "Ask me anything about your driving school.";

/**
 * Your AI Office Manager — reusable chat state/logic hook.
 *
 * Deliberately holds zero UI concerns (no JSX) so `DriveDeskAIWidget.js`
 * stays a thin presentational shell. This is the piece most likely to be
 * copied into a future 815 Digital Services app as-is.
 *
 * v1.3 Wave 2: messages can now carry `kind: "action_plan"` (a proposed
 * plan card, status="proposed") or `kind: "action_plan_completion"` (the
 * post-execution summary appended after a successful approve), alongside
 * the original plain-text `kind: "text"` bubbles. `approvePlanAction` /
 * `rejectPlanAction` are the ONLY two functions in this hook that ever
 * call a mutating endpoint, and both only run in direct response to an
 * explicit user click passed up from the Action Plan UI — nothing here
 * ever approves or executes anything on its own.
 */
export function useAIAssistant({ enabled }) {
  const [messages, setMessages] = useState([]);
  const [rawSuggestions, setRawSuggestions] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [actingPlanId, setActingPlanId] = useState(null);
  const abortRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!enabled) return;
    try {
      const [historyRes, suggestionsRes] = await Promise.all([
        api.get("/ai/chat/history"),
        api.get("/ai/suggestions"),
      ]);
      const historyMessages = (historyRes.data.messages || []).map((m) => ({
        id: nextId(),
        role: m.role,
        content: m.content,
        kind: "text",
      }));
      if (historyMessages.length === 0 && !sessionStorage.getItem(SESSION_GREETED_KEY)) {
        historyMessages.push({ id: nextId(), role: "assistant", kind: "text", content: WELCOME_GREETING });
        sessionStorage.setItem(SESSION_GREETED_KEY, "1");
      }
      setMessages(historyMessages);
      setRawSuggestions(suggestionsRes.data.prompts || []);
    } catch (err) {
      // The widget still works for a fresh conversation even if
      // history/suggestions fail to load (e.g. first-ever use) — but we
      // log it and surface a lightweight, non-blocking note via the
      // existing inline error slot so it isn't a silent failure.
      console.error("Your AI Office Manager: failed to load chat history/suggestions", err);
      setError("Couldn't load your previous conversation — you can still start a new one.");
    } finally {
      setHistoryLoaded(true);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && !historyLoaded) {
      loadHistory();
    }
  }, [enabled, historyLoaded, loadHistory]);

  // v1.3 "Final Demo Pass": once a suggested prompt has been asked (in
  // this conversation OR restored from history), it drops out of the
  // visible chip list so the remaining chips always read as "logical
  // next questions" rather than re-offering something already answered.
  const askedPrompts = useMemo(() => {
    const set = new Set();
    messages.forEach((m) => {
      if (m.role === "user") set.add(m.content.trim().toLowerCase());
    });
    return set;
  }, [messages]);
  const suggestions = useMemo(
    () => rawSuggestions.filter((s) => !askedPrompts.has(s.trim().toLowerCase())),
    [rawSuggestions, askedPrompts]
  );

  // Mutates the `plan` payload embedded in an `action_plan` message
  // in-place (by plan id) — used to reflect status changes (proposed →
  // executing → executed/failed, or → rejected) without touching any
  // other message in the thread.
  const updatePlan = useCallback((planId, updates) => {
    setMessages((prev) =>
      prev.map((m) => (m.kind === "action_plan" && m.plan?.id === planId ? { ...m, plan: { ...m.plan, ...updates } } : m))
    );
  }, []);

  const sendMessage = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setError(null);
    const userMsg = { id: nextId(), role: "user", content: trimmed, kind: "text" };
    const assistantId = nextId();
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: "assistant", content: "", kind: "text" }]);
    setSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    streamAIChat(trimmed, {
      signal: controller.signal,
      onDelta: (delta) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m))
        );
      },
      onActionPlan: (plan) => {
        // Rendered as its own dedicated card, separate from the intro
        // text bubble above it. Status is always "proposed" here —
        // nothing has been changed in the schedule yet.
        setMessages((prev) => [...prev, { id: nextId(), role: "assistant", kind: "action_plan", plan }]);
      },
      onDone: () => {
        setSending(false);
      },
      onError: (err) => {
        setSending(false);
        setError(err?.message || "Your AI Office Manager is temporarily unavailable — please try again.");
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && !m.content
              ? { ...m, content: "Sorry, I couldn't get an answer just now. Please try again." }
              : m
          )
        );
      },
    });
  }, [sending]);

  const approvePlanAction = useCallback(async (planId) => {
    setActingPlanId(planId);
    updatePlan(planId, { status: "executing" });
    try {
      const executed = await approvePlan(planId);
      updatePlan(planId, executed);
      setMessages((prev) => [...prev, { id: nextId(), role: "assistant", kind: "action_plan_completion", plan: executed }]);
      toast.success("Plan executed \u2014 changes have been applied.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Couldn't execute this plan \u2014 please try again.";
      if (status === 423) {
        // Presentation Mode blocked the write — expected, not a bug.
        // Revert to "proposed" so the plan stays re-approvable once a
        // real account is used, instead of getting stuck mid-execution.
        updatePlan(planId, { status: "proposed" });
        toast.warning(detail);
      } else {
        updatePlan(planId, { status: "failed", error: detail });
        toast.error(detail);
      }
    } finally {
      setActingPlanId(null);
    }
  }, [updatePlan]);

  const rejectPlanAction = useCallback(async (planId) => {
    setActingPlanId(planId);
    try {
      const rejected = await rejectPlan(planId);
      updatePlan(planId, rejected);
      toast("Plan rejected \u2014 no changes were made.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reject this plan \u2014 please try again.");
    } finally {
      setActingPlanId(null);
    }
  }, [updatePlan]);

  const clearChat = useCallback(async () => {
    try {
      await api.delete("/ai/chat/history");
    } catch (err) {
      // Best-effort — the chat is already cleared client-side regardless of
      // server-side outcome, so we only log for backend visibility here.
      console.error("Your AI Office Manager: failed to clear chat history on server", err);
    }
    setMessages([]);
    setError(null);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    messages,
    suggestions,
    sending,
    error,
    sendMessage,
    clearChat,
    approvePlanAction,
    rejectPlanAction,
    actingPlanId,
  };
}
